import { Book01Icon, InformationCircleIcon, Loading01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiKeysList } from '@/components/features/api-keys';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { getBackendUrl } from '@/lib/better-auth';

const SERVER_URL = getBackendUrl();
const scalarUrl = `${SERVER_URL}/openapi`;

function ApiReferencePage() {
  const { t } = useTranslation('integrations');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkSpec = async () => {
      try {
        const response = await fetch(`${SERVER_URL}/openapi.json`);
        if (!response.ok) {
          throw new Error('Failed to fetch API specification');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    checkSpec();
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[500px]">
        <div className="flex flex-col items-center gap-4">
          <HugeiconsIcon icon={Loading01Icon} className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">{t('api.title')}...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[500px]">
        <div className="text-center space-y-4">
          <h3 className="text-lg font-semibold">Failed to load API documentation</h3>
          <p className="text-muted-foreground">{error}</p>
          <Button onClick={() => window.location.reload()}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{t('api.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('api.description')}</p>
      </div>

      <Tabs defaultValue="keys" className="space-y-4">
        <TabsList className="flex flex-wrap w-full overflow-x-auto">
          <TabsTrigger value="keys" className="text-xs sm:text-sm">
            {t('api.tabs.keys')}
          </TabsTrigger>
          <TabsTrigger value="guide" className="text-xs sm:text-sm">
            {t('api.tabs.guide')}
          </TabsTrigger>
          <TabsTrigger value="docs" className="text-xs sm:text-sm">
            {t('api.tabs.docs')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="keys" className="space-y-4">
          <Card className="p-6">
            <ApiKeysList />
          </Card>
        </TabsContent>

        <TabsContent value="guide" className="space-y-4">
          <Card className="p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <HugeiconsIcon icon={Book01Icon} className="h-5 w-5" />
              {t('api.guide.title')}
            </h2>
            <div className="space-y-6">
              <div className="space-y-2">
                <h3 className="font-medium text-sm">{t('api.guide.authHeader')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('api.guide.authHeaderDescription', {
                    authHeader: (
                      <code className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono">
                        {t('api.guide.authHeaderName')}
                      </code>
                    ),
                    scheme: (
                      <code className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono">
                        {t('api.guide.authScheme')}
                      </code>
                    ),
                  })}
                </p>
              </div>

              <div className="space-y-2">
                <h3 className="font-medium text-sm">{t('api.guide.exampleCurl')}</h3>
                <div className="bg-slate-950 dark:bg-slate-900 p-4 rounded-lg overflow-x-auto">
                  <pre className="text-xs sm:text-sm text-slate-100 dark:text-slate-200 font-mono">
                    <code>{`curl -X GET ${SERVER_URL}/users \\\n  -H "Authorization: Bearer YOUR_API_KEY_HERE"`}</code>
                  </pre>
                </div>
              </div>

              <div className="space-y-2">
                <h3 className="font-medium text-sm">{t('api.guide.exampleJs')}</h3>
                <div className="bg-slate-950 dark:bg-slate-900 p-4 rounded-lg overflow-x-auto">
                  <pre className="text-xs sm:text-sm text-slate-100 dark:text-slate-200 font-mono">
                    <code>{`const response = await fetch('${SERVER_URL}/users', {
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY_HERE',
    'Content-Type': 'application/json'
  }
})`}</code>
                  </pre>
                </div>
              </div>

              <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950 p-4">
                <div className="flex gap-3">
                  <HugeiconsIcon
                    icon={InformationCircleIcon}
                    className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5"
                  />
                  <div className="text-sm text-blue-800 dark:text-blue-200">
                    {t('api.guide.securityNote', {
                      placeholder: (
                        <code className="bg-white dark:bg-slate-900 px-1.5 py-0.5 rounded text-xs font-mono">
                          {t('api.guide.placeholder')}
                        </code>
                      ),
                    })}
                  </div>
                </div>
              </Card>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="docs" className="space-y-4">
          <Card className="p-6">
            <div className="h-[calc(110vh-20rem)] w-full rounded-lg border overflow-hidden">
              <iframe
                src={scalarUrl}
                className="w-full h-full border-0"
                title={t('api.tabs.docs')}
              />
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/integrations/api')({
  component: ApiReferencePage,
});
